<template>
  <ModalDialog
    ref="dialog"
    title="Edit Order Despatch"
    :submitting="submitting"
    submitTitle="Update"
    icon="fa-truck"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <div v-if="holdProcessLoading">
      <p class="text-center">Placing a hold on the order while editing</p>
      <Spinner />
    </div>

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-show="!holdProcessLoading">
      <NotificationBar type="warning" v-if="isDeliveryItemInvoiced">
        <NotificationBarTitle>The despatch order item has been invoiced and will not be updated</NotificationBarTitle>
      </NotificationBar>

      <FormRow label="Despatch">
        <TaskTypeSelect :value="order.despatchTaskTypeId" @update="updateTaskType" :despatchEnabledOnly="true" placeholder="Select despatch method" :allowNone="true" noneLabel="Select despatch task type" />
      </FormRow>
      <FormRow label="Despatch Location" helpTip="The location for this order. Used for production and despatch planning. Items will be despatched from here">
        <AccountLocationSelect v-model="order.accountLocationId" field="accountLocationId" />
      </FormRow>
      <FormRow label="Despatch Charge Type" helpTip="How despatch fees are charged. Based on task type, address and contact details">
        <FormStaticLabel>{{formatLookupName(order.despatchChargeType, DespatchChargeTypeLookup)}}</FormStaticLabel>
      </FormRow>
      
      <div class="form-group row">
        <label class="col-sm-3 col-form-label">Delivery Fee</label>
        <div class="col-sm-5" v-if="isDespatchChargePerOrder">
          <TaskTypeDespatchStockSelect :taskTypeId="order.despatchTaskTypeId" :value="parseInt(deliveryInfo.stockId || 0)" @input="updateDespatchStockItem" placeholder="Delivery Fee Stock Item" field="deliveryStockId" />
        </div>
        <div class="col-sm-4" v-if="isDespatchChargePerOrder && hasDespatchFeeStock">
          <Spinner v-show="deliveryInfo.loadingPrice" />
          <FormInputCurrency v-show="!deliveryInfo.loadingPrice && !isDeliveryPercentageType" v-model="deliveryInfo.value" placeholder="Fixed delivery fee" />
          <FormInputText type="number" v-show="!deliveryInfo.loadingPrice && isDeliveryPercentageType" v-model="deliveryInfo.value" placeholder="Delivery levy" addon="%" />
        </div>
        <div class="col-sm-6" v-if="isDespatchChargePerTask">
          <FormStaticLabel><span class="text-muted">Customer is charged despatch per task. Default fee items will apply</span></FormStaticLabel>
        </div>
      </div>

      <template v-if="isDespatchTaskTypeLocationEnabled">
        <FormRow label="Delivery Instructions">
          <FormInputText placeholder="Instructions" v-model="order.deliveryInstructions" field="deliveryInstructions" note="Additional instructions or contact info displayed on run sheets" />
        </FormRow>
        <FormHorizontalRule />
        <FormRow label="Delivery Address">
          <FormSearchableSelect
            :options="contactAddressLookup"
            :value="order.contactAddressId"
            @input="updateContactAddress"
            field="contactAddressId"
            :optionSlot="true">
            <template v-slot:option="option">
              <div class="p-1">
                <LabelTags :labelList="option.data.deliveryLabelList" class="float-right" />
                {{option.data.name}}
              </div>
            </template>
          </FormSearchableSelect>
        </FormRow>
        <FormRow label="Delivery Labels" v-if="addressDeliveryLabelList.length > 0">
          <LabelTags :labelList="addressDeliveryLabelList" v-if="!isCustomAddress" />
          <FormLabelTag labelType="contactAddressDelivery" v-model="addressDeliveryLabelList" :singleLabelMode="true" v-if="isCustomAddress" />
        </FormRow>
        <FormRow label="Delivery Address 1">
          <FormInputAddressSearch
            placeholder="Address 1"
            v-model="order.deliveryAddress1"
            field="deliveryAddress1"
            :readonly="!isAddressEditable"
            @updateAddress2="order.deliveryAddress2 = $event"
            @updateSuburb="order.deliverySuburb = $event"
            @updateState="order.deliveryState = $event"
            @updatePostcode="order.deliveryPostcode = $event"
            @updateCountry="order.deliveryCountry = $event" />            
        </FormRow>
        <FormRow label="Delivery Address 2">
          <FormInputText placeholder="Address 2" v-model="order.deliveryAddress2" field="deliveryAddress2" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Suburb">
          <FormInputText placeholder="Suburb" v-model="order.deliverySuburb" field="deliverySuburb" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery State">
          <FormInputText placeholder="State" v-model="order.deliveryState" field="deliveryState" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Postcode">
          <FormInputText placeholder="Postcode" v-model="order.deliveryPostcode" field="deliveryPostcode" :readonly="!isAddressEditable" />
        </FormRow>
        <FormRow label="Delivery Country">
          <FormInputText placeholder="Country" v-model="order.deliveryCountry" field="deliveryCountry" :readonly="!isAddressEditable" />
        </FormRow>
      </template>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import TaskTypeDespatchStockSelect from '@/modules/tasks/components/taskTypeDespatchStockSelect'
import TaskTypeSelect from '@/modules/tasks/components/taskTypeSelect'
import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'

import { PurchaseOrderTypes, PriceTypes, UOMTypes, OrderHoldTypes, LinkTypes, ContactLabelTypes, DespatchChargeTypeLookup, DespatchChargeTypes } from '@/store/types'

export default {
  name: "EditOrderDespatchDialog",
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    TaskTypeDespatchStockSelect,
    TaskTypeSelect,
    AccountLocationSelect
  },
  props: {
    orderId: Number
  },
  data() {
    return {
      holdProcessLoading: true,
      responseInfo: {},
      dialogForm: {},
      submitting: false,
      customerRequestedDate: false,
      deliveryInfo: {
        stockId: null,
        loadingPrice: false,
        quantity: 0,
        units: 0,
        value: 0,
        minChargePrice: 0,
        uomType: '',
        isInvoiced: false,
        hasOverriddenPrice: false
      },
      order: {},
      customerData: {},
      accountData: {},
      despatchTaskTypeList: [],
      addressDeliveryLabelList: []
    }
  },
  created() {
    this.PurchaseOrderTypes = PurchaseOrderTypes
    this.UOMTypes = UOMTypes
    this.DespatchChargeTypeLookup = DespatchChargeTypeLookup
  },
  mounted() {
    
  },
  computed: {
    currentOrderDateEstimateDisplay() {
      return this.$options.filters.formatDate(this.order.dateEstimated)
    },
    selectedDespatchTaskType() {
      return this.despatchTaskTypeList.find(taskType => taskType.id == this.order.despatchTaskTypeId)
    },
    isDespatchTaskTypeLocationEnabled() {
      var taskType = this.selectedDespatchTaskType
      if(taskType)
        return taskType.isLocationTrackingEnabled
      return false
    },
    hasDespatchFeeStock() {
      return this.deliveryInfo.stockId != null && this.deliveryInfo.stockId != 0
    },
    isDeliveryPercentageType() {
      return this.deliveryInfo.uomType == UOMTypes.percentOfLineItem || this.deliveryInfo.uomType == UOMTypes.percentOfOrder
    },
    isDeliveryItemInvoiced() {
      return this.deliveryInfo.isInvoiced
    },
    isDespatchChargePerTask() {
      return this.order.despatchChargeType == DespatchChargeTypes.task
    },
    isDespatchChargePerOrder() {
      return this.order.despatchChargeType == DespatchChargeTypes.order
    },
    canEditRequiredDate() {
      // Can only update the required date if the user has order production approval permission
      return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderProductionApproval)
    },
    contactAddressLookup() {
      if(this.customerData.addresses) {
        var addressLookup = this.customerData.addresses.map(address => {
          return {
            name: address.formattedAddress,
            value: address.id,
            labelList: address.labelList,
            deliveryLabelList: address.deliveryLabelList
          }
        })
        addressLookup = _.concat(addressLookup, { name: 'Custom Address', value: null, labelList: [], deliveryLabelList: [] })
        return addressLookup
      }
      return []
    },
    isCustomAddress() {
      return this.order.contactAddressId == null
    },
    isAddressEditable() {
      return this.isCustomAddress
    },
    selectedAddressData() {
      if(this.customerData.addresses) {
        if(this.customerData.addresses)
          return this.customerData.addresses.find(address => address.id == this.order.contactAddressId)
      }
      return null
    },
  },
  methods: {
    show(order) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.$data.order = _.cloneDeep(order)

      // Find delivery item in the order item list
      this.deliveryInfo.stockId = 0
      this.deliveryInfo.isInvoiced = false

      let orderItem = this.order.orderItems.find(item => item.isDeliveryItem)
      if(orderItem != null) {
        this.deliveryInfo.stockId = orderItem.stockId
        this.deliveryInfo.value = orderItem.unitPrice
        this.deliveryInfo.quantity = orderItem.quantity
        this.deliveryInfo.units = orderItem.units
        this.deliveryInfo.uomType = orderItem.stock ? orderItem.stock.uomType : null
        this.deliveryInfo.isInvoiced = orderItem.isInvoiced
        this.deliveryInfo.hasOverriddenPrice = orderItem.unitPriceOverridden
      }

      this.requestData()

      // Force a hold on the order while editing
      // This prevents further processes from continuing (eg, despatch) while potential edits are in place
      this.$nextTick(() => {
        this.holdProcessLoading = true
        this.$apiService.ordersService.holdOrder(this.$route.params.id, { holdType: OrderHoldTypes.orderEditing, reason: 'Editing Order Despatch' })
        .then(() => {
        })
        .finally(() => {
          this.holdProcessLoading = false
        })
      })

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = {
        despatchTaskTypeId: this.order.despatchTaskTypeId,
        despatchChargeType: this.order.despatchChargeType,
        accountLocationId: this.order.accountLocationId,
        deliveryStockId: this.deliveryInfo.stockId,
        deliveryFeeAmount: this.deliveryInfo.value,
        contactAddressId: this.order.contactAddressId,
        deliveryAddress1: this.order.deliveryAddress1,
        deliveryAddress2: this.order.deliveryAddress2,
        deliverySuburb: this.order.deliverySuburb,
        deliveryState: this.order.deliveryState,
        deliveryPostcode: this.order.deliveryPostcode,
        deliveryCountry: this.order.deliveryCountry,
        deliveryInstructions: this.order.deliveryInstructions
      }

      updateData.deliveryTag = this.isDespatchTaskTypeLocationEnabled ? this.addressDeliveryLabelList : []

      this.$apiService.ordersService.updateOrder(this.orderId, updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },
    lookupDeliveryFeePrice() {
      if(!this.deliveryInfo.stockId) {
        if(!this.deliveryInfo.hasOverriddenPrice)
          this.deliveryInfo.value = 0
        return
      }

      let stockLookupFilter = {
        priceType: PriceTypes.customer,
        contactId: this.$data.order.contactId,
        stockId: this.deliveryInfo.stockId
      }

      // Lookup the default delivery fee
      this.deliveryInfo.loadingPrice = true
      this.$apiService.ordersService.getContactStockLookup(stockLookupFilter)
      .then(response => {
        if(!this.deliveryInfo.hasOverriddenPrice)
          this.deliveryInfo.value = parseFloat(response.data.price.value)

        this.deliveryInfo.minChargePrice = parseFloat(response.data.price.minChargePrice)
        this.deliveryInfo.minChargeItemPrice = parseFloat(response.data.price.minChargeItemPrice)
        this.deliveryInfo.uomType = response.data.stock.uomType
      })
      .finally(() => {
        this.deliveryInfo.loadingPrice = false
      })
    },
    requestData() {
      this.customerData = {}

      var requestList = [
        this.$apiService.coreService.getLabelsForType('contactAddressDelivery'),
        this.$apiService.tasksService.getTaskTypeList({ filter: { isActive: 1, isDespatchEnabled: 1 }})
      ]
      if(this.order.contactId)
        requestList.push(this.$apiService.contactService.getContact(this.order.contactId))

      Promise.all(requestList)
      .then(results => {
        let deliveryTagLabels = results[0].data
        this.$store.commit({
          type: 'label/updateLabels',
          labelType: 'contactAddressDelivery',
          labelList: deliveryTagLabels
        })

        this.despatchTaskTypeList = results[1].data
        if(this.order.contactId)
          this.customerData = results[2].data

        this.updateDefaultDeliveryAddressLabels(true)
      })
      .catch(err => {
        console.error(err)
      })
    },
    updateContactAddress(contactAddressId) {
      this.order.contactAddressId = contactAddressId

      // Fill in the delivery details
      if(this.customerData.addresses) {
        let address = this.customerData.addresses.find(address => address.id == this.order.contactAddressId)
        if(address) {
          this.order.deliveryAddress1 = address.address1
          this.order.deliveryAddress2 = address.address2
          this.order.deliverySuburb = address.suburb
          this.order.deliveryState = address.state
          this.order.deliveryPostcode = address.postcode
          this.order.deliveryCountry = address.country
        }

        // Update default stock item (based on address)
        this.updateDefaultDespatchItem()
      }
      this.updateDefaultDeliveryAddressLabels()
    },
    updateTaskType(taskTypeId) {
      this.order.despatchTaskTypeId = taskTypeId

      // Update default stock item
      this.updateDefaultDespatchItem({ reset: true })
    },
    updateDefaultDeliveryAddressLabels(copyFromOrder = false) {
      // If custom address, find the onsite label & set the label to that
      // Otherwise set it to the default for the selected address
      this.addressDeliveryLabelList = []

      if(this.order.contactAddressId == null) {
        if(copyFromOrder) {
          this.addressDeliveryLabelList = this.order.deliveryTag || []
          
        } else {
          // Default to onsite fixed label if not copying existing labels
          let deliveryLabels = this.$store.getters['label/getLabelsByType'](LinkTypes.contactAddressDelivery)
          let onsiteLabel = deliveryLabels.find(label => label.isFixed && label.statusType == ContactLabelTypes.onsiteAddress)

          if(onsiteLabel)
            this.addressDeliveryLabelList = [onsiteLabel]
        }
      } else {
        let addressData = this.selectedAddressData
        if(addressData && addressData.deliveryLabelList)
          this.addressDeliveryLabelList = addressData.deliveryLabelList
      }
    },
    updateDefaultDespatchItem(options = {}) {
      var contactDefaultStockInfo = null

      // Default to per order despatch charging
      this.order.despatchChargeType = DespatchChargeTypes.order

      // If not resetting & address is empty and stock id is set, just ignore resetting the stock info
      // Causes issues where changing address after setting a delivery stock item would revert the stock item
      if(!options.reset && !this.order.contactAddressId && this.deliveryInfo.stockId) {
        return
      }

      // Get default stock item for the despatch type / contact
      if(_.isArray(this.customerData.contactTaskTypeDefaultStocks)) {
        contactDefaultStockInfo = this.customerData.contactTaskTypeDefaultStocks.find(defaultInfo => defaultInfo.taskTypeId == this.order.despatchTaskTypeId)
      }

      // Get default stock item for the despatch type contact address
      if(_.isArray(this.customerData.addresses)) {
        let curAddress = this.customerData.addresses.find(address => address.id == this.order.contactAddressId)
        if(curAddress && _.isArray(curAddress.contactTaskTypeDefaultStocks)) {
          let defaultAddressStockInfo = curAddress.contactTaskTypeDefaultStocks.find(defaultInfo => defaultInfo.taskTypeId == this.order.despatchTaskTypeId)
          if(defaultAddressStockInfo)
            contactDefaultStockInfo = defaultAddressStockInfo
        }
      }

      if(contactDefaultStockInfo) {
        this.order.despatchChargeType = contactDefaultStockInfo.despatchChargeType
        this.deliveryInfo.hasOverriddenPrice = false

        // If the order has 'Per Task' despatch billing, clear stock id
        if(this.order.despatchChargeType == DespatchChargeTypes.task) {
          this.deliveryInfo.stockId = null
          return
        }

        this.deliveryInfo.stockId = parseInt(contactDefaultStockInfo.stockId)

        // Apply unit price override if set
        if(contactDefaultStockInfo.isPriceOverridden) {
          this.deliveryInfo.hasOverriddenPrice = true
          this.deliveryInfo.value = contactDefaultStockInfo.unitPriceOverride
        } else {
          this.lookupDeliveryFeePrice()
        }

      } else {
        this.deliveryInfo.hasOverriddenPrice = false

        // Grab default stock item for the task type
        if(this.selectedDespatchTaskType && this.selectedDespatchTaskType.defaultFeeStockId) {
          this.updateDespatchStockItem(parseInt(this.selectedDespatchTaskType.defaultFeeStockId))
        }
      }
    },
    updateDespatchStockItem(stockId) {
      // Only called when the user manually updates the stock item
      // Clear settings & re-query price
      this.deliveryInfo.stockId = stockId
      this.deliveryInfo.hasOverriddenPrice = false
      this.deliveryInfo.value = 0

      this.lookupDeliveryFeePrice()
    }
  }
}
</script>